import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import parse from "react-html-parser";
import Header from "../../components/custom/Header";
import { ApiKey, ApiUrl, SessionKey, WebUrl } from "../../util/Constant";
import { numberWithCurrencyFormat, stringIsNullOrEmpty } from "../../util/Util";
import { showResponseMessage } from "../../redux/AppAction";
import ApiEngine from "../../util/ApiEngine";

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatGiveawayDate(value) {
    if (stringIsNullOrEmpty(value)) {
        return "—";
    }
    const parsed = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return String(parsed.getDate()).padStart(2, "0") + " " + months[parsed.getMonth()] + " " + parsed.getFullYear();
}

function formatCount(value) {
    return Number(value || 0).toLocaleString();
}

function earnedLabel(t, count) {
    const total = Number(count) || 0;
    return t(total === 1 ? "GIVEAWAY_EARNED_ONE" : "GIVEAWAY_EARNED", { count: formatCount(total) });
}

const GiveawayDetail = () => {
    const { t } = useTranslation();
    const _history = useHistory();
    const _location = useLocation();
    const _dispatch = useDispatch();
    const id = _location.state && _location.state.id;
    const [campaign, setCampaign] = useState(null);
    const [termsOpen, setTermsOpen] = useState(true);

    useEffect(() => {
        if (!id) {
            _history.replace(WebUrl._URL_EVENT);
            return;
        }
        loadDetail();
    }, [id]);

    const loadDetail = async () => {
        let languageCode = localStorage.getItem(SessionKey._LANGUAGE);
        if (stringIsNullOrEmpty(languageCode) || languageCode === "undefined") {
            languageCode = "en";
        }
        let responseJson = await ApiEngine.get(
            ApiUrl._API_GET_GIVEAWAY_DETAIL + "?id=" + id + "&languageCode=" + languageCode
        );
        if (responseJson[ApiKey._API_SUCCESS_KEY]) {
            setCampaign(responseJson[ApiKey._API_DATA_KEY]);
        }
        else {
            _dispatch(showResponseMessage(false, responseJson[ApiKey._API_MESSAGE_KEY]));
        }
    };

    let showBar = campaign && campaign.targetTicket != null && Number(campaign.targetTicket) > 0;
    let target = showBar ? Number(campaign.targetTicket) : 0;
    let total = campaign ? (Number(campaign.ticketTotal) || 0) : 0;
    let percent = showBar ? Math.round(Math.min(100, (total / target) * 100)) : 0;
    let prizes = campaign && campaign.prizes ? campaign.prizes : [];
    let winners = campaign && campaign.winners ? campaign.winners : [];
    let rows = campaign && campaign.myTransactions ? campaign.myTransactions : [];
    let myTicket = rows.reduce((sum, row) => sum + (Number(row.earnedTicket) || 0), 0);

    return (
        <>
            <Header history={_history} url={WebUrl._URL_EVENT} title={t("GIVEAWAY")} hasBackButton={true} />
            {campaign && (
                <div className="giveaway-page">
                    {!stringIsNullOrEmpty(campaign.giveawayImage) ? (
                        <img className="gw-detail-banner" src={campaign.giveawayImage} alt={campaign.campaignName} />
                    ) : (
                        <div className="gw-detail-banner gw-detail-banner-empty">{campaign.campaignName}</div>
                    )}
                    <section className="gw-info">
                        <h2 className="gw-title">{campaign.campaignName}</h2>
                        {campaign.amountPerTicket != null && Number(campaign.amountPerTicket) > 0 && (
                            <div className="gw-per-ticket"><strong>RM {numberWithCurrencyFormat(parseFloat(campaign.amountPerTicket), 2)}</strong><span>{t("GIVEAWAY_PER_TICKET")}</span></div>
                        )}
                        <div className="gw-dates">
                            <div>
                                <div className="gw-date-label">{t("START_DATE")}</div>
                                <div className="gw-date-value">{formatGiveawayDate(campaign.startDate)}</div>
                            </div>
                            <div>
                                <div className="gw-date-label">{t("END_DATE")}</div>
                                <div className="gw-date-value">{formatGiveawayDate(campaign.endDate)}</div>
                            </div>
                        </div>
                        <div className={showBar ? "gw-progress" : undefined}>
                            {showBar && (
                                <>
                                    <div className="gw-count"><strong>{formatCount(total)}</strong><span>/{formatCount(target)}</span></div>
                                    <div
                                        className="gw-bar"
                                        role="progressbar"
                                        aria-valuemin="0"
                                        aria-valuemax={target}
                                        aria-valuenow={Math.min(total, target)}
                                        aria-label={formatCount(total) + "/" + formatCount(target)}
                                    >
                                        <div className="gw-bar-fill" style={{ width: percent + "%" }} />
                                    </div>
                                    <div className="gw-note">{percent}% {t("GIVEAWAY_ISSUED_NOTE")}</div>
                                </>
                            )}
                            <div className="gw-earned">{earnedLabel(t, myTicket)}</div>
                        </div>
                        {campaign.phase === "ongoing" && (
                            <div className="gw-actions">
                                <button
                                    type="button"
                                    className="gw-join"
                                    onClick={() => _history.push(WebUrl._URL_DEPOSIT)}
                                >
                                    {t("GIVEAWAY_JOIN")}
                                </button>
                            </div>
                        )}
                        {prizes.length > 0 && (
                            <div className="gw-prize-block">
                                <h3 className="gw-section-label">{t("PRIZE")}</h3>
                                <div className="gw-prizes">
                                    {prizes.map((prize) => {
                                        let tone = Number(prize.rank) <= 3 ? " is-" + prize.rank : "";
                                        let prizeWinners = winners.filter((winner) => Number(winner.rank) === Number(prize.rank));
                                        return (
                                            <div key={prize.rank} className={"gw-prize" + tone}>
                                                <span className="gw-prize-rank">{prize.rank}</span>
                                                <span className="gw-prize-copy">
                                                    <span className="gw-prize-name">{prize.prizeName}</span>
                                                    {Number(prize.quantity) > 0 && <span className="gw-prize-qty">x {prize.quantity}</span>}
                                                    {prizeWinners.map((winner, index) => (
                                                        <span key={winner.winnerName + "-" + index} className="gw-winner-line">
                                                            <span className="gw-winner-crown">
                                                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                                                    <path d="M3 18h18v2H3v-2zm1.4-8.6 2.8 3.2L12 6.2l4.8 6.4 2.8-3.2L21 16H3l1.4-6.6z" />
                                                                    <circle cx="5" cy="8" r="1.4" />
                                                                    <circle cx="12" cy="5" r="1.4" />
                                                                    <circle cx="19" cy="8" r="1.4" />
                                                                </svg>
                                                            </span>
                                                            <span className="gw-winner-name">{winner.winnerName}</span>
                                                        </span>
                                                    ))}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <div className="gw-accordion">
                            <button
                                type="button"
                                className={"gw-terms-head" + (termsOpen ? " is-open" : "")}
                                onClick={() => setTermsOpen(!termsOpen)}
                            >
                                <span>{t("GIVEAWAY_TERMS")}</span>
                                <span className="gw-chevron" />
                            </button>
                            {termsOpen && !stringIsNullOrEmpty(campaign.terms) && (
                                <div className="gw-terms-body">{parse(campaign.terms)}</div>
                            )}
                        </div>
                    </section>
                    <div className="gw-records-head">
                        <h3 className="gw-records-title">{t("MY_GIVEAWAY_RECORD")}</h3>
                        <div className="gw-earned gw-records-total">{formatCount(myTicket)} {t("GIVEAWAY_TICKETS")}</div>
                    </div>
                    <div className="gw-table-wrap">
                        <table className="giveaway-table">
                            <thead>
                                <tr>
                                    <th>{t("DATE")}</th>
                                    <th>{t("TRANSACTION_ID")}</th>
                                    <th>{t("AMOUNT")}</th>
                                    <th>{t("EARNED_TICKET")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr>
                                        <td colSpan="4">{t("GIVEAWAY_NOT_JOINED")}</td>
                                    </tr>
                                )}
                                {rows.map((row) => (
                                    <tr key={row.transactionId}>
                                        <td>{row.time}</td>
                                        <td>#{row.transactionId}</td>
                                        <td>{numberWithCurrencyFormat(parseFloat(row.amount), 2)}</td>
                                        <td>{row.earnedTicket}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
};

export default GiveawayDetail;
